import { DotLegalButton } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import React from "react";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { EntityType } from "@common/Common.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { DownloadAllDocumentsButton } from "@common/components/downloadAllDocuments/DownloadAllDocumentsButton";
import AddDocumentsEmptyState from "@common/components/documentsEmptyState/AddDocumentsEmptyState.tsx";
import { useProcessDocumentPanes, useProcessDocuments } from "./ProcessDocument.hooks";
import ProcessDocumentTable from "./ProcessDocumentTable.tsx";

export interface ProcessDocumentsProps {
    processId: string;
    processName?: string;
    // The process's template step shows an empty process as a grey box to click, like the contract documents field;
    // the overview tab sits in its own paper and keeps the table's empty state.
    emptyStateWrappedInPaper: boolean;
    errorText?: string;
}

function ProcessDocuments(props: ProcessDocumentsProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { isLoading, data, hasDocuments, refetchDocuments } = useProcessDocuments(props.processId);
    const { openAddDocumentPane, openEditDocumentPane } = useProcessDocumentPanes(props.processId, refetchDocuments);
    const canAddDocument = permissions.processPermissions.edit && !isNullOrWhitespace(props.processId);

    return (
        <>
            {props.emptyStateWrappedInPaper && !hasDocuments ? (
                <AddDocumentsEmptyState canAdd={canAddDocument} onAddClick={openAddDocumentPane} errorText={props.errorText} />
            ) : (
                <ProcessDocumentTable
                    processId={props.processId}
                    processName={props.processName}
                    isLoading={isLoading}
                    documents={data}
                    refetchDocuments={() => refetchDocuments()}
                    onEditDocument={openEditDocumentPane}
                />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
                <DownloadAllDocumentsButton entityType={EntityType.Process} entityId={props.processId} show={(data?.length ?? 0) > 1} />
                {permissions.processPermissions.edit && (
                    <DotLegalButton buttonType={"primary"} disabled={!canAddDocument} onClick={openAddDocumentPane}>
                        {translateString("addDocument")}
                    </DotLegalButton>
                )}
            </Box>
        </>
    );
}

export default ProcessDocuments;
